import { styled } from '@mui/material';
import { useEffect } from 'react';
import commonConfig from '../../configs/commonConfig';

const StyledPicture = styled('picture')`
  display: flex;
`;

export const makeDataTestId = (dataTestId = 'image') => {
  return {
    prefix: dataTestId,
    wrapper: `${dataTestId}.wrapper`,
    img: `${dataTestId}.img`,
  };
};

export interface ImageProps extends Omit<React.HTMLProps<HTMLPictureElement>, 'as'> {
  dataTestId?: string;
  src: string;
  alt: string;
  height?: string;
  width?: string;
  className?: string;
}

const findWrongExtension = (url: string) => {
  // eslint-disable-next-line custom-plugin/no-png-jpeg-extension
  const wrongExtensions = ['.png', '.jpeg', '.jpg'];

  return wrongExtensions.find((extension) => url.endsWith(extension));
};

const useConsoleErrorIfWrongExtension = (src: string) => {
  useEffect(() => {
    const extension = findWrongExtension(src);

    if (extension) {
      console.error(
        `"${extension}" extension is not supported anymore for images. Please use .webp extension instead.`,
      );
    }
  }, [src]);
};

const PLACEHOLDER_IMAGE = `${commonConfig.baseUrlCDN}/canvas/placeholder.webp`;

const Image: React.FC<ImageProps> = ({
  dataTestId,
  src,
  alt,
  height,
  width,
  className,
  onClick = () => {},
  onError = () => {},
  ...props
}) => {
  useConsoleErrorIfWrongExtension(src);

  const renderedUrl = findWrongExtension(src) ? PLACEHOLDER_IMAGE : src;

  const allDataTestId = makeDataTestId(dataTestId);

  return (
    <StyledPicture data-testid={allDataTestId.wrapper} className={className} onClick={onClick} {...props}>
      <img src={renderedUrl} alt={alt} data-testid={allDataTestId.img} style={{ height, width }} onError={onError} />
    </StyledPicture>
  );
};

export default Image;
